import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Mail, Send } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// IBAN aziendale EDILSTUDIO SRL (lo stesso usato per le distinte SDD)
const IBAN_EDILSTUDIO = 'IT62G0200838083000105614599';

/**
 * QuickEmailDialog
 * Props:
 *   - open / onOpenChange: stato dialog
 *   - to: email destinatario (string)
 *   - toName: nome destinatario (string, opzionale)
 *   - defaultSubject: oggetto precompilato
 *   - defaultBody: corpo precompilato
 *   - contextLabel: etichetta breve (es. "Contratto 20251200001") mostrata in header
 */
const QuickEmailDialog = ({
  open,
  onOpenChange,
  to,
  toName = '',
  defaultSubject = '',
  defaultBody = '',
  contextLabel = '',
}) => {
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(defaultBody);
  const [sending, setSending] = useState(false);

  React.useEffect(() => {
    if (open) {
      setSubject((defaultSubject || '').replace(/\{iban_edilstudio\}/g, IBAN_EDILSTUDIO));
      setBody((defaultBody || '').replace(/\{iban_edilstudio\}/g, IBAN_EDILSTUDIO));
    }
  }, [open, defaultSubject, defaultBody]);

  const handleSend = async () => {
    if (!to) { toast.error('Email destinatario mancante'); return; }
    if (!subject.trim()) { toast.error('Inserisci oggetto'); return; }
    if (!body.trim()) { toast.error('Inserisci messaggio'); return; }

    setSending(true);

    // Sostituisci eventuali variabili residue e prepara il corpo finale
    const finalBody = body.replace(/\{iban_edilstudio\}/g, IBAN_EDILSTUDIO);

    const bodyHtml = `<div style="font-family: Arial, sans-serif; color:#333; max-width:600px;">
<div style="white-space:pre-wrap;">${finalBody.replace(/</g, '&lt;')}</div>
<hr style="margin:24px 0; border:none; border-top:1px solid #ddd;"/>
<table cellpadding="0" cellspacing="0" style="font-size:12px; color:#666;">
<tr><td style="padding-right:12px;">
<strong style="color:#1e40af;">Semplificaffitto.it</strong><br/>
<em>by EDILSTUDIO s.r.l.</em>
</td><td>
Per assistenza: <a href="mailto:info@semplificaffitto.it">info@semplificaffitto.it</a>
</td></tr>
</table>
<div style="margin-top:16px;">
<img src="cid:semplificaffitto_logo" alt="Semplificaffitto.it" style="max-width:180px; height:auto;" />
</div>
<p style="margin-top:16px; font-size:10px; color:#999; line-height:1.5; text-align:justify;">
Qualora non foste i destinatari, vogliate immediatamente informarci con lo stesso mezzo ed eliminare il messaggio, con gli eventuali allegati, senza trattenerne copia. Qualsivoglia utilizzo non autorizzato del contenuto di questo messaggio costituisce violazione dell'obbligo di non prendere cognizione della corrispondenza tra altri soggetti, salvo pi&ugrave; grave illecito ed espone il responsabile alle relative conseguenze civili e penali.
</p>
</div>`;

    try {
      const { data } = await axios.post(`${API_URL}/api/admin/email/send`, {
        to: [to],
        subject,
        body_text: finalBody,
        body_html: bodyHtml,
        reply_to: 'info@semplificaffitto.it',
      });
      toast.success(`✓ Email inviata a ${data.recipients.length} destinatari (incluso archivio)`);
      onOpenChange(false);
    } catch (e) {
      toast.error(`Errore: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="quick-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-600" /> Email rapida
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="text-sm bg-slate-50 p-3 rounded-lg">
            <div><span className="text-slate-500">A:</span> <strong>{toName || to}</strong> <span className="text-slate-400">&lt;{to}&gt;</span></div>
            {contextLabel && <div className="text-xs text-slate-500 mt-1">Riferimento: {contextLabel}</div>}
            <div className="text-xs text-slate-400 mt-1">BCC archivio: info@semplificaffitto.it (automatico)</div>
          </div>

          <div>
            <Label className="mb-1 block">Oggetto</Label>
            <Input value={subject} onChange={e => setSubject(e.target.value)} data-testid="quick-email-subject" />
          </div>

          <div>
            <Label className="mb-1 block">Messaggio</Label>
            <Textarea value={body} onChange={e => setBody(e.target.value)} rows={10} data-testid="quick-email-body" />
            <p className="text-xs text-slate-400 mt-1">
              La firma "Semplificaffitto.it by EDILSTUDIO s.r.l." viene aggiunta automaticamente.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>Annulla</Button>
          <Button onClick={handleSend} disabled={sending} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="quick-email-send">
            <Send className="w-4 h-4 mr-2" /> {sending ? 'Invio...' : 'Invia'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuickEmailDialog;
